import { IGeogridData } from '../api/IApiResponse';
import { GEOGRID_CONFIG } from '../config/GeogridConfig';
import { EProjectType } from '../enum/project';

/**
 * GeoGrid subgrade bearing capacity safety factor FS (geoGrid.subgrade.safetyFactor): the rules that read, keep and
 * migrate it live here, so the calculation, the form mapping, the project load and the form submit agree.
 */

/**
 * Version of the FS input stored in geoGrid.safetyFactorInputVersion. Version 1: the calculation uses the FS of the form
 * and the GeoGrid prefill is the Excel default 3. Without it the geoGrid was saved before, when the calculation always
 * used 3 and Railway / Airport prefilled 3.75 / 3.5.
 */
export const GEOGRID_SAFETY_FACTOR_INPUT_VERSION = 1;

// Prefills written before version 1 (Railway and Airport GeoGrid), which the calculation never used
const LEGACY_SAFETY_FACTOR_PREFILLS: Partial<Record<string, number>> = {
    [EProjectType.railways]: 3.75,
    [EProjectType.airport]: 3.5,
};

/** The FS as a finite number > 0 (a numeric string from the form included), otherwise undefined. */
export const parseGeogridSafetyFactor = (safetyFactor: unknown): number | undefined => {
    const numericValue = typeof safetyFactor === 'string' && safetyFactor.trim() !== '' ? Number(safetyFactor) : safetyFactor;
    return typeof numericValue === 'number' && Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

/**
 * FS as the calculation uses it: a missing or invalid FS gives the Excel default 3, so the membrane term
 * 60·π·CBR/FS never becomes NaN or Infinity.
 */
export const resolveGeogridSafetyFactor = (safetyFactor: unknown): number => parseGeogridSafetyFactor(safetyFactor) ?? GEOGRID_CONFIG.DEFAULT_SAFETY_FACTOR;

/**
 * A valid FS in the saved geoGrid is kept whatever the run/modified flags say: after a reload those flags are reset and
 * cannot tell a user's FS from a prefilled one. The per-type prefill applies only when the saved FS is missing or invalid.
 * @returns the same geoGrid when nothing changes, otherwise a copy with the saved FS
 */
export const keepSavedGeogridSafetyFactor = (savedGeoGrid: IGeogridData | undefined, geoGridData: IGeogridData): IGeogridData => {
    const savedSafetyFactor = parseGeogridSafetyFactor(savedGeoGrid?.subgrade?.safetyFactor);
    if (savedSafetyFactor === undefined || geoGridData.subgrade?.safetyFactor === savedSafetyFactor) {
        return geoGridData;
    }
    return { ...geoGridData, subgrade: { ...geoGridData.subgrade, safetyFactor: savedSafetyFactor } };
};

/** Marks a geoGrid written by the current form or calculation, so a later load never migrates its FS. */
export const markGeogridSafetyFactorInputVersion = (geoGridData: IGeogridData): IGeogridData => ({
    ...geoGridData,
    safetyFactorInputVersion: GEOGRID_SAFETY_FACTOR_INPUT_VERSION,
});

/**
 * Project load: a geoGrid saved before version 1 with the old Railway 3.75 / Airport 3.5 prefill gets the Excel default 3
 * (the value the calculation used), then the version marker. A marked geoGrid is left as it is, so an FS of 3.75 or 3.5
 * chosen afterwards survives every reload.
 * @returns the same geoGrid when it is already marked, otherwise a marked copy
 */
export const migrateLegacyGeogridSafetyFactor = (projectType: string | undefined, geoGridData: IGeogridData | undefined): IGeogridData | undefined => {
    if (!geoGridData || geoGridData.safetyFactorInputVersion !== undefined) {
        return geoGridData;
    }
    const legacyPrefill = projectType === undefined ? undefined : LEGACY_SAFETY_FACTOR_PREFILLS[projectType];
    const migrated =
        legacyPrefill !== undefined && parseGeogridSafetyFactor(geoGridData.subgrade?.safetyFactor) === legacyPrefill
            ? { ...geoGridData, subgrade: { ...geoGridData.subgrade, safetyFactor: GEOGRID_CONFIG.DEFAULT_SAFETY_FACTOR } }
            : geoGridData;
    return markGeogridSafetyFactorInputVersion(migrated);
};
